import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import { Location } from 'history';
import { RCIcon } from '@reverbdotcom/cadence/components';
import { SlidersIcon } from '@reverbdotcom/cadence/icons/react';

import DismissibleSidebar from './dismissible_sidebar';
import RQLFacets from './rql_facets';
import { Filter, WidgetType } from './search_filters';
import { useUser } from '../user_hooks';
import SelectFacet from './select_facet';
import FilterChip from './filter_chip';
import CoreLink from './core_link';
import { MParticleEventName, trackEvent } from '../elog/mparticle_tracker';

interface IProps {
  filters: Filter[];
  loading?: boolean;
  subtitle?: string;
  trackingQuery?: string;
  location: Location;
  hideClearFiltersButton?: boolean;
  excludedFilterKeys?: string[];
  toggleExposedSidebarOpen?: () => void;
  showNewMobileSidebar?: boolean;
  isFilterOpenByDefault?: (filter: Filter) => boolean;
  clearFiltersUrl?: string;
  selectedFiltersTotal?: number;
}

export default function MarketplaceFilterSidebar({
  filters = [],
  loading = false,
  subtitle = '',
  trackingQuery = '',
  location = null,
  hideClearFiltersButton = false,
  toggleExposedSidebarOpen = undefined,
  showNewMobileSidebar = false,
  isFilterOpenByDefault = undefined,
  clearFiltersUrl = undefined,
  selectedFiltersTotal = 0,
}: IProps) {

  const sort = filters.find(filter => filter.widgetType === WidgetType.SORT);
  const user = useUser();
  const { pathname } = location;

  const filtersToShow = filters.filter(f => {
    const key = f.aggregationName || f.key;
    if (pathname?.includes('/shop') && key === 'SELLER_LOCATION') return false;
    if (pathname?.includes('/p/') && key === 'SHOW_ONLY') return false;
    if (key === 'LOCATION' && user.shippingRegionCode !== 'US_CON') return false;
    return true;
  });

  const trackFilterChipClick = (opened) => {
    trackEvent({
      componentName: 'FilterChip',
      eventName: MParticleEventName.ClickedSearchFilterChip,
      displayStyle: opened ? 'opened' : 'closed',
      parent: 'ALL',
      query: trackingQuery,
    });
  };

  return (
    <>
      <div className="marketplace-filter marketplace-filter--all-filters">
        <DismissibleSidebar
          title={I18n.t('commons.marketplaceFilters.filterYourSearch')}
          subtitle={subtitle}
          footerButtonText={I18n.t('commons.marketplaceFilters.viewResults')}
          onToggle={trackFilterChipClick}
          trigger={(
            <div className={showNewMobileSidebar ? 'mobile-d-none d-inline' : ''}>
              <FilterChip
                loading={loading}
                count={selectedFiltersTotal}
                mobileOnlyCount
                onClick={toggleExposedSidebarOpen}
              >
                <RCIcon svgComponent={SlidersIcon} />
                <span>
                  {I18n.t('commons.marketplaceFilters.allFilters')}
                </span>
              </FilterChip>
            </div>
          )}
        >
          {selectedFiltersTotal > 0 && !hideClearFiltersButton &&
            <ClearFiltersButton
              clearFilterUrl={clearFiltersUrl || pathname}
              hideOnMobile={false}
              hideOnDesktop
            />}
          {sort &&
            <div className="d-none mobile-d-block pb-4 bdb-1 bd-color-primary">
              <SelectFacet
                pageSection="sidebar"
                filter={sort}
                trackingQuery={trackingQuery}
              />
            </div>
          }
          <RQLFacets
            filters={filtersToShow}
            loading={loading}
            trackingQuery={trackingQuery}
            isFilterOpenByDefault={isFilterOpenByDefault}
          />
        </DismissibleSidebar>
      </div>
      {selectedFiltersTotal > 0 && !hideClearFiltersButton &&
        <ClearFiltersButton
          clearFilterUrl={clearFiltersUrl || pathname}
          hideOnMobile
        />
      }
    </>
  );
}

export function ClearFiltersButton({ clearFilterUrl, hideOnMobile = false, hideOnDesktop = false }) {
  const className = classNames(
    'marketplace-filter',
    {
      'mobile-d-none': hideOnMobile,
      'd-none mobile-d-inline': hideOnDesktop,
    },
  );

  return (
    <div className={className}>
      <CoreLink
        to={clearFilterUrl}
        className="filter-chip filter-chip--text-link"
      >
        {I18n.t('commons.marketplaceFilters.clearFilters')}
      </CoreLink>
    </div>
  );
}
